/**
 * Header Component
 * Top navigation bar with app mode switcher and user controls
 */

import { useState } from 'react';
import { ListMusic, History, Shuffle, User, LogOut, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUIStore, type AppMode } from '../../stores/ui';
import { useAuthStatus, useLogout } from '../../api/hooks/useAuth';
import { useAuthStore } from '../../stores/auth';
import { useWhatsNew } from '../../hooks/useWhatsNew';

const MODES = [
  { id: 'workbench' as const, label: 'Workbench', icon: ListMusic },
  { id: 'history' as const, label: 'History', icon: History },
  { id: 'sequencer' as const, label: 'Sequencer', icon: Shuffle },
] as const;

export function Header() {
  const { mode, setMode } = useUIStore();
  const { data: authStatus } = useAuthStatus();
  const logout = useLogout();
  const navigate = useNavigate();
  const { clearTokens } = useAuthStore();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { version } = useWhatsNew();

  const handleModeClick = (newMode: AppMode) => {
    setMode(newMode);
  };

  const handleLogout = () => {
    // Clear tokens immediately from localStorage (always works)
    clearTokens();

    // Try to call logout API (best effort, ignore errors)
    logout.mutate(undefined, {
      onSettled: () => {
        // Redirect to login after API call completes (success or error)
        setUserMenuOpen(false);
        navigate('/login', { replace: true });
      },
    });

    // If API call hangs, force redirect after 2 seconds
    setTimeout(() => {
      setUserMenuOpen(false);
      navigate('/login', { replace: true });
    }, 2000);
  };

  return (
    <header className="h-14 border-b border-zinc-800 flex items-center justify-between px-6">
      {/* Left: Logo + App Name + Version */}
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
          <ListMusic className="w-5 h-5 text-white" />
        </div>
        <div className="flex items-baseline gap-2">
          <h1 className="text-xl font-semibold text-zinc-100">Splister</h1>
          <span className="text-xs text-zinc-500 font-medium">v{version}</span>
        </div>
      </div>

      {/* Center: Mode Switcher */}
      <div className="flex items-center gap-1 bg-zinc-900 rounded-lg p-1">
        {MODES.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => handleModeClick(id)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-md transition-all duration-200
              ${
                mode === id
                  ? 'bg-emerald-500 text-white shadow-sm'
                  : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800'
              }
            `}
            aria-pressed={mode === id}
          >
            <Icon className="w-4 h-4" />
            <span className="text-sm font-medium">{label}</span>
          </button>
        ))}
      </div>

      {/* Right: User Menu */}
      <div className="flex items-center gap-3">
        {/* Spotify Connection Status */}
        {authStatus?.authenticated && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 rounded-full border border-emerald-500/20">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            <span className="text-xs font-medium text-emerald-400">Spotify Connected</span>
          </div>
        )}

        {/* User Dropdown */}
        <div className="relative">
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-zinc-800 transition-colors"
            aria-expanded={userMenuOpen}
            aria-haspopup="true"
          >
            <div className="w-8 h-8 bg-zinc-700 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-zinc-300" />
            </div>
            <span className="text-sm font-medium text-zinc-300">
              {authStatus?.user?.displayName || 'Guest'}
            </span>
            <ChevronDown className="w-4 h-4 text-zinc-400" />
          </button>

          {/* Dropdown Menu */}
          {userMenuOpen && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-10"
                onClick={() => setUserMenuOpen(false)}
              />

              {/* Menu */}
              <div className="absolute right-0 mt-2 w-56 bg-zinc-800 rounded-lg shadow-xl border border-zinc-700 py-1 z-20">
                <div className="px-4 py-3 border-b border-zinc-700">
                  <p className="text-sm font-medium text-zinc-100">
                    {authStatus?.user?.displayName || 'Guest'}
                  </p>
                  <p className="text-xs text-zinc-400 mt-0.5">
                    {authStatus?.user?.id || 'Not connected'}
                  </p>
                </div>

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
